import React from 'react';
import { Modal, Typography, Card, Row, Col, Statistic, Space, Badge, Divider, Table, Tag, Button } from 'antd';
import { format } from 'date-fns';
import { 
    CheckCircleOutlined, 
    ExclamationCircleOutlined, 
    CloseCircleOutlined,
    WarningOutlined,
    DollarOutlined,
    CalendarOutlined,
    BarcodeOutlined,
    GlobalOutlined,
    CreditCardOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    IdcardOutlined,
    CloseOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

const PaymentDetailModal = ({ payment, visible, onClose }) => {
    if (!payment) return null;

    const formatDate = (dateString) => {
        return dateString ? format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss') : '';
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'succeeded':
                return <CheckCircleOutlined />;
            case 'disputed':
                return <WarningOutlined />;
            case 'refunded':
                return <ExclamationCircleOutlined />;
            case 'failed':
                return <CloseCircleOutlined />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'succeeded':
                return '#52c41a';
            case 'disputed':
                return '#fa541c';
            case 'refunded':
                return '#faad14';
            case 'failed':
                return '#f5222d';
            default:
                return '#1890ff';
        }
    };
    
    const productColumns = [
        {
            title: 'Product ID',
            dataIndex: 'product_id',
            key: 'product_id',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
        },
        {
            title: 'Unit Amount',
            dataIndex: 'unit_amount',
            key: 'unit_amount',
            align: 'right',
            render: (text) => `${text} ${payment.currency}`
        },
        {
            title: 'Total',
            key: 'total',
            align: 'right',
            render: (_, record) => `${(record.unit_amount * record.quantity).toFixed(2)} ${payment.currency}`
        }
    ];

    // Calculate payment percentages for the visual breakdown
    const subTotal = payment.sub_total || 0;
    const taxAmount = payment.tax_amount || 0;
    const discountAmount = payment.discount_amount || 0;
    const total = payment.total || 0;

    const taxPercentage = (taxAmount / subTotal * 100).toFixed(1);
    const discountPercentage = (discountAmount / subTotal * 100).toFixed(1);
    
    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            centered
            className="payment-detail-modal"
            bodyStyle={{ 
                padding: 0, 
                maxHeight: '85vh', 
                overflowY: 'auto',
                borderRadius: '8px'
            }}
            closeIcon={<CloseOutlined style={{ color: 'white', fontSize: 16 }} />}
        >
            <div className="payment-modal-header" style={{ 
                background: '#1890ff',
                padding: '16px 24px',
                color: 'white',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Title level={4} style={{ margin: 0, color: 'white' }}>Payment Receipt</Title>
                <Button type="text" icon={<CloseOutlined />} onClick={onClose} style={{ color: 'white' }} />
            </div>
            
            <div style={{ padding: '24px' }}>
                {/* Summary Section */}
                <Card bordered={false} className="payment-summary-card" style={{ 
                    marginBottom: 24,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
                }}>
                    <Row gutter={[24, 24]} justify="space-between">
                        <Col xs={24} md={10}>
                            <Statistic
                                title={<Text style={{ fontSize: 14 }}>Total Paid</Text>}
                                value={payment.total}
                                prefix={<DollarOutlined />}
                                suffix={payment.currency}
                                valueStyle={{ 
                                    color: getStatusColor(payment.payment_status), 
                                    fontSize: 32, 
                                    fontWeight: 700 
                                }}
                            />
                            <Space style={{ marginTop: 6 }}>
                                <Tag color={getStatusColor(payment.payment_status)} icon={getStatusIcon(payment.payment_status)}>
                                    {payment.payment_status}
                                </Tag>
                                <Tag color="blue">{payment.payment_type}</Tag>
                            </Space>
                        </Col>
                        <Col xs={0} md={2}>
                            <Divider type="vertical" style={{ height: '100%' }} />
                        </Col>
                        <Col xs={24} md={12}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Payment Date</Text>
                                        <Space>
                                            <CalendarOutlined />
                                            <Text>{formatDate(payment.payment_date)}</Text>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col span={12}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Payment ID</Text>
                                        <Space>
                                            <IdcardOutlined />
                                            <Text style={{ fontSize: 14 }}>{payment.id}</Text>
                                        </Space>
                                    </Space>
                                </Col>
                                {payment.promotion_code && (
                                    <Col span={24}>
                                        <Space direction="vertical" size={1}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Promotion Applied</Text>
                                            <Tag color="gold">{payment.promotion_code}</Tag>
                                        </Space>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Card>

                {/* Payment Breakdown Visual */}
                <Card 
                    title={<Space><InfoCircleOutlined /> Payment Breakdown</Space>} 
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="payment-breakdown-card"
                >
                    <div className="payment-breakdown">
                        <Row align="middle" style={{ marginBottom: 12 }}>
                            <Col span={6}>
                                <Text>Subtotal</Text>
                            </Col>
                            <Col span={12}>
                                <div style={{ height: 16, background: '#e6f7ff', borderRadius: 8, width: '100%' }} />
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong>{payment.sub_total} {payment.currency}</Text>
                            </Col>
                        </Row>
                        
                        <Row align="middle" style={{ marginBottom: 12 }}>
                            <Col span={6}>
                                <Text>Tax ({taxPercentage}%)</Text>
                            </Col>
                            <Col span={12}>
                                <div style={{ 
                                    height: 16, 
                                    background: '#ffd591', 
                                    borderRadius: 8,
                                    width: `${taxPercentage}%`,
                                    minWidth: 20
                                }} />
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong>{payment.tax_amount} {payment.currency}</Text>
                            </Col>
                        </Row>
                        
                        {payment.discount_amount > 0 && (
                            <Row align="middle" style={{ marginBottom: 12 }}>
                                <Col span={6}>
                                    <Text>Discount ({discountPercentage}%)</Text>
                                </Col>
                                <Col span={12}>
                                    <div style={{ 
                                        height: 16, 
                                        background: '#ff7875', 
                                        borderRadius: 8,
                                        width: `${discountPercentage}%`,
                                        minWidth: 20
                                    }} />
                                </Col>
                                <Col span={6} style={{ textAlign: 'right' }}>
                                    <Text strong>-{payment.discount_amount} {payment.currency}</Text>
                                </Col>
                            </Row>
                        )}

                        <Divider style={{ margin: '16px 0' }} />
                        
                        <Row align="middle">
                            <Col span={6}>
                                <Text strong>Total</Text>
                            </Col>
                            <Col span={12} />
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong style={{ color: getStatusColor('succeeded'), fontSize: 18 }}>
                                    {payment.total} {payment.currency}
                                </Text>
                            </Col>
                        </Row>

                        {payment.coins_amount > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <Tag color="gold" style={{ padding: '4px 8px' }}>
                                    <Space>
                                        <DollarOutlined />
                                        <span>Earned {payment.coins_amount} coins from this transaction</span>
                                    </Space>
                                </Tag>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Products Table */}
                <Card 
                    title={<Space><ShoppingOutlined /> Products Purchased</Space>} 
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="products-card"
                >
                    <Table 
                        dataSource={payment.products} 
                        columns={productColumns} 
                        pagination={false}
                        rowKey="product_id"
                        size="small"
                    />
                </Card>

                {/* Billing Address */}
                <Card 
                    title={<Space><GlobalOutlined /> Billing Address</Space>} 
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="billing-address-card"
                >
                    <Row gutter={[16, 8]}>
                        <Col span={24}>
                            <Text strong>{payment.billing_address.address_line1}</Text>
                        </Col>
                        {payment.billing_address.address_line2 && (
                            <Col span={24}>
                                <Text>{payment.billing_address.address_line2}</Text>
                            </Col>
                        )}
                        <Col span={24}>
                            <Space>
                                <Text>{payment.billing_address.city},</Text>
                                <Text>{payment.billing_address.state}</Text>
                                <Text>{payment.billing_address.postal_code}</Text>
                            </Space>
                        </Col>
                        <Col span={24}>
                            <Text>{payment.billing_address.country}</Text>
                        </Col>
                    </Row>
                </Card>

                {/* Conditional Cards for Special Statuses */}
                {payment.is_refunded && (
                    <Card 
                        title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} /> Refund Information</Space>} 
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="refund-card"
                        headStyle={{ backgroundColor: '#fffbe6', borderBottom: '1px solid #ffe58f' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Amount Refunded</Text>
                                    <Text strong style={{ color: '#faad14', fontSize: 18 }}>
                                        {payment.refund.amount} {payment.currency}
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Refunded At</Text>
                                    <Text>{formatDate(payment.refund.refunded_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Reason</Text>
                                    <div style={{ 
                                        background: '#fffbe6', 
                                        padding: '8px 12px', 
                                        borderRadius: 4,
                                        border: '1px solid #ffe58f' 
                                    }}>
                                        <Text>{payment.refund.reason}</Text>
                                    </div>
                                </Space>
                            </Col>
                            {payment.refund.failure_reason && (
                                <Col span={24}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary">Failure Reason</Text>
                                        <div style={{ 
                                            background: '#fff2e8', 
                                            padding: '8px 12px', 
                                            borderRadius: 4,
                                            border: '1px solid #ffccc7' 
                                        }}>
                                            <Text type="danger">{payment.refund.failure_reason}</Text>
                                        </div>
                                    </Space>
                                </Col>
                            )}
                        </Row>
                    </Card>
                )}

                {payment.is_disputed && (
                    <Card 
                        title={<Space><WarningOutlined style={{ color: '#fa541c' }} /> Dispute Information</Space>} 
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="dispute-card"
                        headStyle={{ backgroundColor: '#fff2e8', borderBottom: '1px solid #ffbb96' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Amount Disputed</Text>
                                    <Text strong style={{ color: '#fa541c', fontSize: 18 }}>
                                        {payment.dispute.amount} {payment.currency}
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Dispute Status</Text>
                                    <Tag color="volcano">{payment.dispute.status}</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Disputed At</Text>
                                    <Text>{formatDate(payment.dispute.disputed_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Reason for Dispute</Text>
                                    <div style={{ 
                                        background: '#fff2e8', 
                                        padding: '8px 12px', 
                                        borderRadius: 4,
                                        border: '1px solid #ffbb96' 
                                    }}>
                                        <Text>{payment.dispute.reason}</Text>
                                    </div>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                )}

                {payment.has_error && (
                    <Card 
                        title={<Space><CloseCircleOutlined style={{ color: '#f5222d' }} /> Payment Failure</Space>} 
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="failure-card"
                        headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Failed At</Text>
                                    <Text>{formatDate(payment.failure.failed_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">Error Message</Text>
                                    <div style={{ 
                                        background: '#fff1f0', 
                                        padding: '8px 12px', 
                                        borderRadius: 4,
                                        border: '1px solid #ffa39e' 
                                    }}>
                                        <Text type="danger">{payment.failure.message}</Text>
                                    </div>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                )}
            </div>
        </Modal>
    );
};

export default PaymentDetailModal;